import { BLOG_API_URL } from '../../../config';
import type { Post, PostInput, PostList, PostStatus } from './contracts/post';

/** Thrown when the blog API returns a non-2xx response. */
export class BlogApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'BlogApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Cognito id token; sent as `Authorization: Bearer <token>` on write calls. */
  token?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers['authorization'] = `Bearer ${opts.token}`;

  const res = await fetch(`${BLOG_API_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = await res.json();
      message = data.message ?? data.error ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new BlogApiError(res.status, message);
  }

  // Tolerate empty bodies on any 2xx (e.g. DELETE may return 200/204 with no
  // content); only parse JSON when there's actually a body to parse.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${search.toString()}`;
}

// ---- Public reads -------------------------------------------------------

export function listPosts(
  params: { tag?: string; cursor?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<PostList> {
  return request<PostList>(`/posts${qs(params)}`, { signal });
}

export function getPost(slug: string, signal?: AbortSignal): Promise<Post> {
  return request<Post>(`/posts/${encodeURIComponent(slug)}`, { signal });
}

// ---- Admin (JWT-guarded) ------------------------------------------------

export function listAdminPosts(
  token: string,
  params: { status?: PostStatus; cursor?: string; limit?: number } = {},
): Promise<PostList> {
  return request<PostList>(`/admin/posts${qs(params)}`, { token });
}

export function getAdminPost(token: string, slug: string): Promise<Post> {
  return request<Post>(`/admin/posts/${encodeURIComponent(slug)}`, { token });
}

export function createPost(token: string, input: PostInput): Promise<Post> {
  return request<Post>('/posts', { method: 'POST', body: input, token });
}

export function updatePost(token: string, slug: string, input: PostInput): Promise<Post> {
  return request<Post>(`/posts/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: input,
    token,
  });
}

export function deletePost(token: string, slug: string): Promise<void> {
  return request<void>(`/posts/${encodeURIComponent(slug)}`, { method: 'DELETE', token });
}

// ---- Asset uploads (JWT-guarded presign + direct PUT) -------------------

interface PresignResponse {
  uploadUrl: string;
  key: string;
  expiresInSeconds: number;
  /** CDN URL the object is readable at. Only returned when the API has an assets CDN configured. */
  publicUrl?: string;
}

/** Presign, PUT the bytes to S3, and return the object key + its public URL. */
export async function uploadAsset(
  token: string,
  file: File,
): Promise<{ key: string; url: string }> {
  const presign = await request<PresignResponse>('/assets/presign-upload', {
    method: 'POST',
    body: { fileName: file.name, contentType: file.type },
    token,
  });

  const put = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!put.ok) {
    throw new BlogApiError(put.status, `Upload failed: ${put.statusText}`);
  }

  // The assets bucket is private: the upload URL is readable only because it
  // carries a short-lived signature, so it can't be reused for display. The CDN
  // URL is the only durable one, and it exists only if the API has a CDN wired up.
  // Status 0: this is a client-side precondition, not a status the API returned.
  if (!presign.publicUrl) {
    throw new BlogApiError(
      0,
      'Upload succeeded but the API returned no public URL — the blog assets CDN is not configured.',
    );
  }

  return { key: presign.key, url: presign.publicUrl };
}
